/**
 * Timezone-safe date helpers.
 *
 * ⚠️ Gotcha inherited from the Wave9 Hub (Phase 16): `valid_until` must be the
 * END of a calendar day in Europe/London, stored as UTC. Naively adding 30 days
 * to `new Date()` expires quotes an hour early (or late) across a BST boundary,
 * and a quote that dies at 00:59 on the morning it was meant to be valid is the
 * kind of bug a partner only reports after losing the deal.
 *
 * No date library — Intl gives us the London offset for any instant.
 */

const LONDON = 'Europe/London'

const fmt = new Intl.DateTimeFormat('en-GB', {
  timeZone: LONDON,
  hour12: false,
  year: 'numeric', month: '2-digit', day: '2-digit',
  hour: '2-digit', minute: '2-digit', second: '2-digit',
})

function londonParts(instant: Date) {
  const p = Object.fromEntries(
    fmt.formatToParts(instant).filter((x) => x.type !== 'literal').map((x) => [x.type, x.value]),
  ) as Record<string, string>
  return {
    year: Number(p.year), month: Number(p.month), day: Number(p.day),
    hour: Number(p.hour === '24' ? '00' : p.hour), minute: Number(p.minute), second: Number(p.second),
  }
}

/** Offset of Europe/London from UTC, in minutes, at the given instant (+60 during BST). */
export function londonOffsetMinutes(instant: Date): number {
  const p = londonParts(instant)
  const asIfUTC = Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute, p.second)
  return Math.round((asIfUTC - instant.getTime()) / 60000)
}

/** The calendar date in London for a given instant, as {year, month, day}. */
export function londonCalendarDate(instant: Date) {
  const p = londonParts(instant)
  return { year: p.year, month: p.month, day: p.day }
}

/**
 * The UTC instant of 23:59:59.999 London on the London-calendar day that is
 * `addDays` after `from`. This is what goes in quotes.valid_until.
 */
export function endOfLondonDay(from: Date, addDays = 0): Date {
  const { year, month, day } = londonCalendarDate(from)
  // Build the target wall-clock time, then solve for the UTC instant. One
  // correction pass is enough: the offset can only shift by an hour, and a DST
  // transition never lands at 23:59.
  const wall = Date.UTC(year, month - 1, day + addDays, 23, 59, 59, 999)
  let guess = wall - londonOffsetMinutes(new Date(wall)) * 60000
  const settled = wall - londonOffsetMinutes(new Date(guess)) * 60000
  if (settled !== guess) guess = settled
  return new Date(guess)
}

/** Default quote validity, editable per quote (decision 15). 30 → 60 by decision 251 (2026-09-28);
 *  the live figure is the Organisation setting `quote_validity_days` — this is the fallback. */
export const DEFAULT_VALIDITY_DAYS = 60

export function defaultValidUntil(from: Date = new Date(), days = DEFAULT_VALIDITY_DAYS): Date {
  return endOfLondonDay(from, days)
}

/** True when a quote has passed its validity. Nightly job flips status; this is the read-side check. */
export function isExpired(validUntil: Date | string | null, now: Date = new Date()): boolean {
  if (!validUntil) return false
  const d = validUntil instanceof Date ? validUntil : new Date(validUntil)
  return d.getTime() < now.getTime()
}

/** Whole days remaining, London-calendar aware. Negative once expired. */
export function daysUntil(validUntil: Date | string, now: Date = new Date()): number {
  const d = validUntil instanceof Date ? validUntil : new Date(validUntil)
  const a = endOfLondonDay(now, 0).getTime()
  const b = endOfLondonDay(d, 0).getTime()
  return Math.round((b - a) / 86400000)
}

/** dd/mm/yyyy in London — the format UK schools expect. */
export function formatUKDate(d: Date | string): string {
  const date = d instanceof Date ? d : new Date(d)
  return new Intl.DateTimeFormat('en-GB', {
    timeZone: LONDON, day: '2-digit', month: '2-digit', year: 'numeric',
  }).format(date)
}

/**
 * Decision 201: Wave9's financial year runs 1 April → 31 March. Returns the
 * 1 April (UTC midnight) that starts the FY containing `d`.
 */
export function fyStartOf(d: Date): Date {
  const y = d.getUTCMonth() >= 3 ? d.getUTCFullYear() : d.getUTCFullYear() - 1
  return new Date(Date.UTC(y, 3, 1))
}
